import { PropsWithChildren, useContext, useMemo } from 'react'
import { FiX } from 'react-icons/fi'

import { Modal } from '~/components/Modal'
import {
  computeDiscountValue,
  formatDiscount,
  numberToBRL,
} from '~/modules/sales/services'
import { getLocalDate } from '~/utils/getLocalDate'

import { subscriptionsContext } from '../../..'
import { CancelButton } from '../CancelButton'
import { PaymentCard } from './PaymentCard'

type Props = {
  visible: boolean
  onClose: () => void
}

export const SubscriptionModal: React.FC<PropsWithChildren<Props>> = ({
  visible,
  onClose,
  children,
}) => {
  const { subscription, isValid } = useContext(subscriptionsContext)

  const totalPlanValue = useMemo(() => {
    const sum =
      subscription?.plan?.metaData.items?.reduce((accu, item) => {
        accu += item.quantity * (item.pricing_scheme?.price ?? item.price ?? item.amount ?? 0)
        return accu
      }, 0) || 0
    return sum
  }, [subscription?.plan])

  return (
    <Modal visible={visible}>
      <div className='flex justify-between items-center mb-6'>
        <div className='flex gap-3 items-center'>
          <h2 className='text-xl font-medium'>Assinatura</h2>
          {isValid ? null : (
            <span className='py-1 px-3 rounded-lg text-gpa-red bg-red-100'>
              {subscription?.paymentStatus === 'pending'
                ? 'Aguardando pagamento'
                : subscription?.status === 'canceled'
                ? 'Cancelada'
                : new Date(subscription!.termEnd) < new Date()
                ? 'Expirada/Atrasada'
                : 'Inativa'}
            </span>
          )}
        </div>
        <button
          className='bg-white hover:bg-gpa-gray-50 p-1 rounded-full'
          onClick={onClose}
        >
          <FiX size={24} />
        </button>
      </div>

      <div className='flex flex-col gap-1 mb-8'>
        <div className='flex justify-between items-center'>
          <p>
            <strong>
              {subscription?.plan?.name ?? subscription?.metaData.planName}
            </strong>
          </p>

          <p>
            Quantidade de licenças:{' '}
            {subscription?.plan?.licensesAmount ||
              subscription?.metaData.licensesAmount ||
              0}
          </p>
        </div>
        <div className='flex gap-4'>
          <p>
            <span className='text-2xl'>&bull;</span>
            <span className='font-semibold'>Início</span>:{' '}
            {getLocalDate(subscription?.createdAt ?? '')}
          </p>
          <p>
            <span className='text-2xl'>&bull;</span>
            <span className='font-semibold'>Data da Renovação</span>:{' '}
            {getLocalDate(subscription?.termEnd ?? '')}
          </p>
        </div>
      </div>

      <p className='mb-2 font-semibold'>Produtos da assinatura:</p>
      <div className='flex w-full p-3 rounded-md border border-gpa-blue-50 overflow-x-auto'>
        <table className='table-fixed w-full bg-gpa-blue-50 min-w-[500px]'>
          <thead>
            <tr className='text-left'>
              <th>Nome</th>
              <th>Quantidade</th>
              <th>Preço unitário</th>
              <th>Preço total</th>
            </tr>
          </thead>
          <tbody>
            {(subscription?.plan
              ? subscription.plan
              : subscription
            )?.metaData.items?.map((item, index) => (
              <tr
                key={`subscription-item-${index}`}
                className='even:bg-slate-100 odd:bg-white'
              >
                <td>{item.name}</td>
                <td>{item.quantity}</td>
                <td>{numberToBRL((item.pricing_scheme?.price ?? item.price ?? item.amount ?? 0) / 100)}</td>
                <td>{numberToBRL(totalPlanValue / 100)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {subscription?.subscriptionDiscount?.length ? (
        <>
          <p className='m-2 font-semibold'>Descontos</p>
          <div className='flex w-full p-3 rounded-md border border-gpa-blue-50 overflow-x-auto'>
            <table className='table-fixed w-full bg-gpa-blue-50 min-w-[500px]'>
              <thead>
                <tr className='text-left'>
                  <th>Cupom</th>
                  <th>Desconto</th>
                  <th>Valor</th>
                  <th>Aplicações</th>
                </tr>
              </thead>
              <tbody>
                {subscription?.subscriptionDiscount?.map(
                  ({ discount }, index) => (
                    <tr
                      key={`subscription-discount-item-${index}`}
                      className='even:bg-slate-100 odd:bg-white'
                    >
                      <td>{discount.code}</td>
                      <td>{formatDiscount(discount.type, discount.value)}</td>
                      <td>
                        {numberToBRL(
                          computeDiscountValue(
                            discount.type,
                            discount.value,
                            totalPlanValue,
                          ) / 100,
                        )}
                      </td>
                      <td>
                        {discount.cycles
                          ? `próxima(s) ${discount.cycles} cobrança(s)`
                          : 'Sempre'}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      <br />

      {subscription?.plan ? (
        <>
          <h3 className='text-lg font-semibold text-slate-800 mb-2'>
            Editar Cartão
          </h3>
          <PaymentCard />
        </>
      ) : null}

      {children}

      {subscription?.status === 'canceled' ? null : (
        <CancelButton onCancel={onClose} />
      )}
      <button
        type='button'
        className='w-full px-3 py-2  rounded-lg font-semibold
            text-gpa-blue-500 hover:bg-slate-50 transition-colors
              duration-200 mt-5'
        onClick={onClose}
      >
        Fechar
      </button>
    </Modal>
  )
}
